import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import * as types from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { adaptToForm } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { adaptTemplateBasedOnSections, adaptTemplateForNEType, updateTemplateFieldsValues } from 'routes/TargetDeliveryTemplateAccessFunction/utils/targetDeliveryTemplateFields.adapters';
import { TargetDeliveryTemplateInterfaceFormData } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import * as _ from 'lodash';
import TargetDeliveryTemplateAccessFunctionForm from '../../components/TargetDeliveryTemplateAccessFunctionForm';
import { adaptTemplateOptions, adaptTemplateVisibilityMode } from '../../utils/targetDeliveryTemplate.adapter';
import targetTemplates from '../../templates';

type RouterParams = {
  history: string
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDeliveryTemplateAccessFunctionFormData
}

const mapState = (state: RootState) => ({
  selectedTargetDeliveryTemplate: selectors.getSelectedTargetDeliveryTemplateAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  deliveryFunctionId: state.global.selectedDF,
  privileges: state.authentication.privileges,
  beforeLogout: state.authentication.beforeLogout,
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  targetDeliveryTemplate: selectors.getTargetDeliveryTemplateAccessFunction(state),
});

const mapDispatch = {
  goToTargetDeliveryTemplateAccessFunctionList: actions.goToTargetDeliveryTemplateAccessFunctionList,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  goToTargetDeliveryTemplateAccessFunctionCreate: actions.goToTargetDeliveryTemplateAccessFunctionCreate,
  goToTargetDeliveryTemplateAccessFunctionEdit: actions.goToTargetDeliveryTemplateAccessFunctionEdit,
  goToTargetDeliveryTemplateAccessFunctionView: actions.goToTargetDeliveryTemplateAccessFunctionView,
  selectTargetDeliveryTemplateAccessFunction: actions.selectTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunctionList: actions.getTargetDeliveryTemplateAccessFunctionList,
  deleteTargetDeliveryTemplateAccessFunction: actions.deleteTargetDeliveryTemplateAccessFunction,
  updateTargetDeliveryTemplateAccessFunction: actions.updateTargetDeliveryTemplateAccessFunction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getTemplates: warrantActions.getAllTemplates,
  getTargetDeliveryTemplateAccessFunction: actions.getTargetDeliveryTemplateAccessFunction,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDeliveryTemplateAccessFunctionView = ({
  deliveryFunctionId,
  history,
  accessFunctions,
  enabledAccessFunctions,
  selectedTargetDeliveryTemplate,
  targetDeliveryTemplate,
  goToTargetDeliveryTemplateAccessFunctionList,
  getTargetDeliveryTemplateAccessFunction,
  goToTargetDeliveryTemplateAccessFunctionEdit,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [formData, setFormData] = useState<Partial<types.TargetDeliveryTemplateAccessFunctionFormData>>({});
  const { t } = useTranslation();

  useEffect(() => {
    const temp = selectedTargetDeliveryTemplate?.[0];

    if (!temp) {
      return;
    }
    getTargetDeliveryTemplateAccessFunction(deliveryFunctionId, temp.id as string);
  }, [
    deliveryFunctionId,
    selectedTargetDeliveryTemplate,
    getTargetDeliveryTemplateAccessFunction,
  ]);

  useEffect(() => {

    if (!targetDeliveryTemplate || !selectedTargetDeliveryTemplate || selectedTargetDeliveryTemplate.length <= 0) {
      return;
    }

    const newFormData = adaptToForm(targetDeliveryTemplate);

    const availableTemplates = adaptTemplateOptions(
      targetTemplates,
      accessFunctions,
      enabledAccessFunctions,
    );

    setTemplates(availableTemplates);

    const templateKey = targetDeliveryTemplate.afType ?? Object.keys(availableTemplates)[0];
    const adaptedTemplate = adaptTemplateVisibilityMode(availableTemplates[templateKey], 'view');
    let newTemplate = cloneDeep(adaptedTemplate);

    if ((newFormData.targetInformation != null) && !_.isEmpty(newFormData.targetInformation) && (newFormData.targetInformation[0].ACC != null)) {
      newTemplate = adaptTemplateBasedOnSections(adaptedTemplate, 'ACC', (newFormData.targetInformation[0].ACC as string), 'targetInformation');
    }

    if ((newTemplate as FormTemplate).key === 'ERICSSON_NE') {
      const general = (newFormData.templateSections || []).find((section) => section.name === 'EIMSGENERAL');
      const neType = (general?.options || []).find((o) => o.key === 'netype');
      newTemplate = adaptTemplateForNEType(newTemplate as FormTemplate, neType?.value as string || '')
    }
    if (targetDeliveryTemplate.templateSections != null && targetDeliveryTemplate.afType === 'GENBANDC20') {
      const sections = targetDeliveryTemplate.templateSections.filter(
        (section) => section.name === 'callContentChannels',
      );
      if (sections != null) {

        const sanreq = sections[0].options?.find((option) => option.key === 'SANREQ');
        const prependrtedigs = sections[0].options?.find((option) => option.key === 'PREPENDRTEDIGS');

        if (sanreq != null) {
          newTemplate = adaptTemplateBasedOnSections(newTemplate, 'SANREQ', (sanreq?.value as string), 'callContentChannels');
        }
        if (prependrtedigs != null) {
          newTemplate = adaptTemplateBasedOnSections(newTemplate, 'PREPENDRTEDIGS', (prependrtedigs?.value as string), 'callContentChannels');
        }
      }
    }

    if (newFormData && newTemplate && templateKey === 'SIEMENS_LIMS') {
      const lea = (newFormData.ICD as TargetDeliveryTemplateInterfaceFormData[])[0].LEA as string;
      (newTemplate.ICD as FormTemplateSection).fields.LEA.options = [{ label: lea, value: lea }];
      (newTemplate.ICD as FormTemplateSection).fields.LEA.initial = lea;

      // Get ACC from selected template
      if (selectedTargetDeliveryTemplate?.length > 0) {
        const targetInfoSection = selectedTargetDeliveryTemplate[0]
          .templateSections?.find((section) => section.name === 'targetInformation')
        const acc: string = targetInfoSection?.options.find((option) => option.key === 'ACC')?.value as string;

        // Update Network-Call Content type
        const networkSection = selectedTargetDeliveryTemplate[0]
          .templateSections?.find((section) => section.name === 'networkType')
        networkSection?.options.forEach((network) => {
          ((newTemplate as FormTemplate).networkType as FormTemplateSection).fields[network.key].initial = network.value
        })
        updateTemplateFieldsValues(newTemplate as FormTemplate, acc);
      }
      newTemplate.formId = Date.now().toString();
    }

    if (newFormData && newTemplate && templateKey === 'RIBBONC20_CSC') {
      const definition = String((newFormData.usnbdCallContentChannelInformation as TargetDeliveryTemplateInterfaceFormData[])
        ?.at(0)?.definition ?? '');

      if (definition === 'PAIRED') {
        (newTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
        (newTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
      } else {
        (newTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined DN';
        (newTemplate.usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
      }
    }

    setFormData(newFormData);
    setTemplate(newTemplate);

  }, [
    accessFunctions,
    enabledAccessFunctions,
    selectedTargetDeliveryTemplate,
    targetDeliveryTemplate,
  ]);

  const handleCancel = () => goToTargetDeliveryTemplateAccessFunctionList(history);

  const handleFieldEdit = () => goToTargetDeliveryTemplateAccessFunctionEdit(history);

  if (!template) {
    return <Fragment />;
  }

  return (
    <div data-testid="TargetDeliveryTemplateAccessFunctionCreate">
      <TargetDeliveryTemplateAccessFunctionForm
        ref={formRef}
        isEditing={false}
        template={template}
        data={formData}
        title={t('targetDeliveryTemplateAccessFunction.form.view')}
        onClickEdit={handleFieldEdit}
        onClickCancelClose={handleCancel}
      />
    </div>
  );
};

export default withRouter(connector(TargetDeliveryTemplateAccessFunctionView));
